import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import { useSeo } from "@/hooks/useSeo";
import { getAllVideos, searchVideos } from "@/lib/videoService";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 芭乐视频` : "全站搜索 - 芭乐视频",
    description: keyword
      ? `芭乐视频全站搜索“${keyword}”的相关影视内容，支持电影、电视剧、动漫等类别关键词模糊匹配。`
      : "在芭乐视频全站搜索电影、电视剧、动漫等高清影视内容，输入关键词即可快速查找。",
    keywords: "芭乐视频搜索,影视搜索,电影搜索,电视剧搜索,动漫搜索",
  });

  const results = useMemo(() => searchVideos(keyword), [keyword]);

  const hotWords = useMemo(
    () => getAllVideos().slice().sort((a, b) => b.heat - a.heat).slice(0, 8).map((v) => v.title),
    []
  );

  const recommend = useMemo(
    () => getAllVideos().slice().sort((a, b) => b.heat - a.heat).slice(0, 6),
    []
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("q", input.trim());
    setParams(next);
  };

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800 font-medium">搜索</span>
      </nav>

      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">搜索影视内容</h1>
      <p className="mt-2 text-sm text-foreground-500">
        输入片名、演员、导演或题材关键词，支持模糊匹配快速查找。
      </p>

      <form onSubmit={submit} className="mt-6 max-w-2xl">
        <div className="flex flex-col sm:flex-row items-stretch gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              name="keyword"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索电影、电视剧、动漫…"
              className="w-full pl-10 pr-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
              aria-label="搜索关键词"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            <i className="ri-search-line" /> 搜索
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">热门搜索：</span>
          {hotWords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => {
                setInput(word);
                setParams({ q: word });
              }}
              className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-700 transition-colors"
            >
              {word}
            </button>
          ))}
        </div>
      </form>

      {keyword ? (
        results.length > 0 ? (
          <section className="mt-9">
            <p className="text-sm text-foreground-600">
              关于“<span className="font-medium text-primary-600">{keyword}</span>”的搜索结果，共找到
              <span className="font-label font-semibold text-foreground-900"> {results.length} </span>部作品
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5 mt-6">
              {results.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </section>
        ) : (
          <section className="mt-9">
            <div className="rounded-lg bg-background-100 border border-background-200/70 py-14 flex flex-col items-center text-center px-4">
              <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到相关内容</h3>
              <p className="mt-2 text-sm text-foreground-500 max-w-md">
                换个关键词试试，或看看下面这些热门作品，说不定会有惊喜。
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-home-4-line" /> 返回首页
              </Link>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                为你推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mt-5">
                {recommend.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>
        )
      ) : (
        <section className="mt-9 rounded-lg bg-background-100 border border-background-200/70 py-14 flex flex-col items-center text-center px-4">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-200 text-primary-500">
            <i className="ri-search-line text-3xl" />
          </span>
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">开始你的探索</h3>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">
            输入关键词，或点击上方热门搜索，快速找到你想看的内容。
          </p>
        </section>
      )}
    </div>
  );
}